import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import logo from '../../asset/logo/large-white.svg';
import { authAxios, useAuth } from '../../auth';
import './Populate.css';
import { InputForm } from './InputForm';
import { AudioPlayButton } from '../shared/AudioPlayButton';
import { API } from '../../config';
import { parseWordCharacters, getTypeColor, getTypeLabel, getTransliteration } from './characterInfo';

import {
    Layout,
    Divider,
    Menu,
    Dropdown,
    Table,
    Button,
    Modal,
    Tooltip,
    Input,
    Switch
} from 'antd';
import { DownOutlined, LeftOutlined, RightOutlined, ReadOutlined, UserOutlined, LogoutOutlined } from '@ant-design/icons';

// Interface for manuscript word data
interface ManuscriptWord {
    chapter: number;
    verse: number;
    position: number;
    word: string;
    is_punctuation?: number;  // 1 if punctuation, 0 or undefined if word
}

const { Header, Content, Footer } = Layout;

// Old Testament books (Hebrew) in canonical order
const ot_books = [
    'genesis', 'exodus', 'leviticus', 'numbers', 'deuteronomy',
    'joshua', 'judges', 'ruth', '1samuel', '2samuel',
    '1kings', '2kings', '1chronicles', '2chronicles', 'ezra',
    'nehemiah', 'esther', 'job', 'psalms', 'proverbs',
    'ecclesiastes', 'songofsolomon', 'isaiah', 'jeremiah', 'lamentations',
    'ezekiel', 'daniel', 'hosea', 'joel', 'amos',
    'obadiah', 'jonah', 'micah', 'nahum', 'habakkuk',
    'zephaniah', 'haggai', 'zechariah', 'malachi',
];

// New Testament books (Greek) in canonical order
const nt_books = [
    'matthew', 'mark', 'luke', 'john', 'acts',
    'romans', '1corinthians', '2corinthians', 'galatians', 'ephesians',
    'philippians', 'colossians', '1thessalonians', '2thessalonians', '1timothy',
    '2timothy', 'titus', 'philemon', 'hebrews', 'james',
    '1peter', '2peter', '1john', '2john', '3john',
    'jude', 'revelation',
];

// Display names for books that need special formatting
const bookDisplayNames: { [key: string]: string } = {
    'songofsolomon': 'Song of Solomon',
    '1samuel': '1 Samuel',
    '2samuel': '2 Samuel',
    '1kings': '1 Kings',
    '2kings': '2 Kings',
    '1chronicles': '1 Chronicles',
    '2chronicles': '2 Chronicles',
    '1corinthians': '1 Corinthians',
    '2corinthians': '2 Corinthians',
    '1thessalonians': '1 Thessalonians',
    '2thessalonians': '2 Thessalonians',
    '1timothy': '1 Timothy',
    '2timothy': '2 Timothy',
    '1peter': '1 Peter',
    '2peter': '2 Peter',
    '1john': '1 John',
    '2john': '2 John',
    '3john': '3 John',
};

// Helper to get display name for a book
export const getBookDisplayName = (book: string): string => {
    if (bookDisplayNames[book]) {
        return bookDisplayNames[book];
    }
    return ToTitleCase(book);
};

// Helper to determine language from book name
const getLangFromBook = (book: string): string => {
    return ot_books.includes(book) ? 'hebrew' : 'greek';
};

export const ToTitleCase = ((str: string | undefined) =>
    str === undefined ? '' : str.toLowerCase().split(' ').map((word: string) =>
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' '));

// Cache for glosses to avoid repeated API calls
const glossCache: { [key: string]: string } = {};

// Check if definition_in_context is valid (exists and not all asterisks)
const isValidDefinition = (def: string | null | undefined): boolean => {
    if (!def || def.trim() === '') return false;
    // Check if it's all asterisks (placeholder)
    if (/^\*+$/.test(def.trim())) return false;
    return true;
};

// WordLink component with hover tooltip showing definition in context or gloss
const WordLink: React.FC<{
    verseData: any;
    setDefinition: Function;
    setSelectedOccurrence: Function;
    lang: string;
    setHighlightPosition?: Function;
}> = ({ verseData, setDefinition, setSelectedOccurrence, lang, setHighlightPosition }) => {
    // Use definition_in_context if valid, otherwise we'll need to fetch gloss
    const hasValidDefinition = isValidDefinition(verseData.definition_in_context);
    const [tooltipText, setTooltipText] = React.useState<string>(
        hasValidDefinition ? verseData.definition_in_context : 'Loading...'
    );
    const [hasFetched, setHasFetched] = React.useState<boolean>(hasValidDefinition);

    // Reset state when verseData changes (e.g., switching books/verses)
    React.useEffect(() => {
        const valid = isValidDefinition(verseData.definition_in_context);
        setTooltipText(valid ? verseData.definition_in_context : 'Loading...');
        setHasFetched(valid);
    }, [verseData.id, verseData.word, verseData.definition_in_context]);

    const handleMouseEnter = () => {
        // If we already have a valid definition_in_context, no need to fetch
        if (hasFetched) return;

        const cacheKey = `${lang}:${verseData.word}`;

        // Check cache first
        if (glossCache[cacheKey] !== undefined) {
            setTooltipText(glossCache[cacheKey] || 'No definition available');
            setHasFetched(true);
            return;
        }

        // Fetch gloss from API
        const glossaryUrl = lang === 'hebrew' ? API.hebrewGlossary(verseData.word) : API.greekGlossary(verseData.word);
        authAxios.get(glossaryUrl)
            .then((res: { data: any }) => {
                const gloss = res.data?.gloss || '';
                glossCache[cacheKey] = gloss;
                setTooltipText(gloss || 'No definition available');
            })
            .catch(() => {
                glossCache[cacheKey] = '';
                setTooltipText('No definition available');
            })
            .finally(() => {
                setHasFetched(true);
            });
    };

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        const glossaryUrl = lang === 'hebrew' ? API.hebrewGlossary(verseData.word) : API.greekGlossary(verseData.word);
        authAxios.get(glossaryUrl)
            .then((res: { data: any }) => {
                setDefinition(res.data);
                setSelectedOccurrence(verseData);
                // Highlight the corresponding position in exemplar
                if (setHighlightPosition) {
                    setHighlightPosition(verseData.position);
                }
            })
            .catch(err => console.log(err));
    };

    return (
        <Tooltip title={tooltipText}>
            <a
                onMouseEnter={handleMouseEnter}
                onClick={handleClick}
                style={{ color: 'black', cursor: 'pointer' }}
            >
                {verseData.word}
            </a>
        </Tooltip>
    );
};

const columns = (lang: string) => [
    {
        title: 'Word',
        key: 'word',
        dataIndex: 'word',
        render: (text: string) => (
            <span className={lang} style={{ fontSize: 18 }}>{text}</span>
        ),
    },
    {
        title: 'Definition in Context',
        key: 'definition_in_context',
        dataIndex: 'definition_in_context',
    },
    {
        title: 'Lexical Form',
        key: 'lexical_form',
        dataIndex: 'lexical_form',
        render: (text: string) => (
            <span className={lang} style={{ fontSize: 18 }}>{text}</span>
        ),
    },
    {
        title: 'Gloss',
        key: 'gloss',
        dataIndex: 'gloss',
    },
    {
        title: 'Part of Speech',
        key: 'part_of_speech',
        dataIndex: 'part_of_speech',
    },
    {
        title: 'Declension',
        key: 'declension',
        dataIndex: 'declension',
    },
    {
        title: 'Case',
        key: 'case',
        dataIndex: 'case',
    },
    {
        title: 'Number',
        key: 'number',
        dataIndex: 'number',
    },
    {
        title: 'Gender',
        key: 'gender',
        dataIndex: 'gender',
    },
    {
        title: 'Mood',
        key: 'mood',
        dataIndex: 'mood',
    },
    {
        title: 'Voice',
        key: 'voice',
        dataIndex: 'voice',
    },
    {
        title: 'Tense',
        key: 'tense',
        dataIndex: 'tense',
    },
    {
        title: 'Person',
        key: 'person',
        dataIndex: 'person',
    },
    {
        title: 'Stem',
        key: 'stem',
        dataIndex: 'stem',
    },
    {
        title: 'Logged',
        key: 'occurences',
        dataIndex: 'occurences',
        align: 'right',
    },
];

const filterColumns = (definition: any, lang: string, word: string | null) => {
    const allColumns: any[] = columns(lang);
    // Replace the Word column with one that includes the AudioPlayButton
    const wordColumnIndex = allColumns.findIndex(c => c.key === 'word');
    if (wordColumnIndex >= 0 && word) {
        allColumns[wordColumnIndex] = {
            ...allColumns[wordColumnIndex],
            render: (text: string) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={lang} style={{ fontSize: 18 }}>{text}</span>
                    <AudioPlayButton language={lang} word={word} size="small" />
                </span>
            ),
        };
    }

    let showColumns: any[] = [];
    allColumns.forEach((c: any) => {
        let check = definition[c.key];
        if (check != null && check.toString().length > 0) {
            showColumns.push(c);
        }
    });

    return showColumns;
}

function Populate() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Get initial values from URL params or defaults
    const initialBook = searchParams.get('book') || '3john';
    const initialChapter = parseInt(searchParams.get('chapter') || '1', 10);
    const initialVerse = parseInt(searchParams.get('verse') || '1', 10);

    const [loading, setLoading] = useState<boolean>(true);
    const [lang, setLang] = useState<string>(getLangFromBook(initialBook));
    const [allVerses, setAllVerses] = useState<any[]>([]); // All verses in the chapter
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [book, setBook] = useState(initialBook);
    const [totalChapters, setTotalChapters] = useState<any[]>([]);
    const [chapter, setChapter] = useState(initialChapter);
    const [verse, setVerse] = useState(initialVerse);
    const [totalVerses, setTotalVerses] = useState<number[]>([]);
    const [definition, setDefinition] = useState<any>({});
    const [selectedOccurrence, setSelectedOccurrence] = useState<any>(null);
    const [editData, setEditData] = useState<any>(null);
    const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
    const [bookFilter, setBookFilter] = useState<string>('');
    const [showManuscript, setShowManuscript] = useState<boolean>(true);
    const [manuscriptData, setManuscriptData] = useState<ManuscriptWord[]>([]);
    const [highlightPosition, setHighlightPosition] = useState<number>(1);
    const [charAnalysisWord, setCharAnalysisWord] = useState<string | null>(null);
    const [isCharModalVisible, setIsCharModalVisible] = useState<boolean>(false);

    // Track the last URL we set to avoid infinite loops
    const lastSetUrl = useRef<string>('');

    // Get words for the current verse only
    const currentVerseWords = allVerses.filter(v => v.verse === verse);

    // Update URL when book, chapter, or verse changes (but not if we just synced from URL)
    useEffect(() => {
        const newUrl = `${book}-${chapter}-${verse}`;
        if (lastSetUrl.current !== newUrl) {
            lastSetUrl.current = newUrl;
            setSearchParams({ book, chapter: chapter.toString(), verse: verse.toString() });
        }
    }, [book, chapter, verse, setSearchParams]);

    // Sync state from URL params (handles browser back/forward)
    useEffect(() => {
        const urlBook = searchParams.get('book') || '3john';
        const urlChapter = parseInt(searchParams.get('chapter') || '1', 10);
        const urlVerse = parseInt(searchParams.get('verse') || '1', 10);
        const urlKey = `${urlBook}-${urlChapter}-${urlVerse}`;

        // Only update if URL differs from what we last set (i.e., user navigated via back/forward)
        if (lastSetUrl.current !== urlKey) {
            lastSetUrl.current = urlKey;
            setBook(urlBook);
            setLang(getLangFromBook(urlBook));
            setChapter(urlChapter);
            setVerse(urlVerse);
            clearSelection();
            setRefreshKey(prev => prev + 1);
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        authAxios.get(API.chapters(book))
            .then((res: { data: any; }) => {
                // Handle empty or null response gracefully
                setTotalChapters(res.data || []);
            }).catch(e => {
                console.log(e);
                setTotalChapters([]); // Allow adding first chapter
            });
        authAxios.get(API.bible(book, chapter))
            .then((res: { data: any; }) => {
                let v = res.data || [];
                if (Array.isArray(v)) {
                    v.sort((a: any, b: any) =>
                        a.chapter - b.chapter || a.verse - b.verse || a.position - b.position
                    );
                } else {
                    v = []; // Handle null/non-array response
                }
                setAllVerses(v);
                // Extract unique verse numbers
                const uniqueVerses = Array.from(new Set(v.map((item: any) => item.verse))).sort((a: any, b: any) => a - b) as number[];
                setTotalVerses(uniqueVerses);
                setLoading(false);
            }).catch(e => {
                console.log(e);
                setAllVerses([]);
                setTotalVerses([]);
                setLoading(false); // Still stop loading so user can add first word
            });
    }, [refreshKey, book, chapter]);

    // Fetch manuscript data when toggle is on and book/chapter changes
    useEffect(() => {
        if (showManuscript) {
            authAxios.get(API.manuscript(book, chapter))
                .then((res: { data: ManuscriptWord[] }) => {
                    setManuscriptData(res.data || []);
                })
                .catch(e => {
                    console.log('Manuscript not available:', e);
                    setManuscriptData([]);
                });
        } else {
            setManuscriptData([]);
        }
    }, [showManuscript, book, chapter]);

    // Reset highlight position when verse changes (to next position based on logged words)
    useEffect(() => {
        const nextPos = currentVerseWords.length > 0
            ? currentVerseWords[currentVerseWords.length - 1].position + 1
            : 1;
        setHighlightPosition(nextPos);
    }, [verse, currentVerseWords.length]);

    // Clear selection state
    const clearSelection = () => {
        setDefinition({});
        setSelectedOccurrence(null);
        setEditData(null);
        setIsEditModalVisible(false);
        // Reset highlight to next available position
        const nextPos = currentVerseWords.length + 1;
        setHighlightPosition(nextPos);
    };

    // Navigation helpers
    const goToPrevVerse = () => {
        const currentIndex = totalVerses.indexOf(verse);
        if (currentIndex > 0) {
            setVerse(totalVerses[currentIndex - 1]);
            clearSelection();
        } else if (chapter > 1) {
            // Go to previous chapter's last verse
            setChapter(chapter - 1);
            setVerse(999); // Will be corrected when data loads
            setRefreshKey(refreshKey + 1);
            clearSelection();
        }
    };

    const goToNextVerse = () => {
        const currentIndex = totalVerses.indexOf(verse);
        if (currentIndex < totalVerses.length - 1) {
            setVerse(totalVerses[currentIndex + 1]);
            clearSelection();
        } else {
            // Go to next chapter's first verse
            const currentChapterIndex = totalChapters.findIndex((c: any) => c.chapter === chapter);
            if (currentChapterIndex < totalChapters.length - 1) {
                setChapter(totalChapters[currentChapterIndex + 1].chapter);
                setVerse(1);
                setRefreshKey(refreshKey + 1);
                clearSelection();
            }
        }
    };

    // Handle clicking Update button - populate form with word data and show modal
    const handleUpdateClick = () => {
        if (definition && selectedOccurrence) {
            // Combine glossary data with occurrence data
            setEditData({
                id: selectedOccurrence.id,
                word: selectedOccurrence.word,
                definition_in_context: selectedOccurrence.definition_in_context,
                position: selectedOccurrence.position,
                // Glossary data
                lexical_form: definition.lexical_form,
                gloss: definition.gloss,
                part_of_speech: definition.part_of_speech,
                declension: definition.declension,
                person: definition.person,
                tense: definition.tense,
                voice: definition.voice,
                mood: definition.mood,
                case: definition.case,
                number: definition.number,
                gender: definition.gender,
                degree: definition.degree,
                // Hebrew-specific
                state: definition.state,
                stem: definition.stem,
                class: definition.class,
            });
            setIsEditModalVisible(true);
        }
    };

    // Correct verse if it doesn't exist (e.g., after navigating to previous chapter)
    // But allow "next verse" (max + 1) for adding new verses
    useEffect(() => {
        if (totalVerses.length > 0 && !totalVerses.includes(verse)) {
            const maxVerse = Math.max(...totalVerses);
            const nextVerse = maxVerse + 1;

            // Allow the next verse (for adding new verses)
            if (verse === nextVerse) {
                return; // This is valid - user wants to add a new verse
            }

            // If verse is too high (e.g., 999 or more than next), go to last verse
            if (verse > nextVerse) {
                setVerse(maxVerse);
            } else {
                setVerse(totalVerses[0] || 1);
            }
        }
    }, [totalVerses, verse]);

    return (
        <div className="Populate">
            <Layout className="layout" style={{ minHeight: '100vh' }}>
                <Header className="Populate-Header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <img src={logo} className="Populate-logo" alt="logo" />
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                        <Link to={`/reader?book=${book}&chapter=${chapter}`}>
                            <Button type="primary" icon={<ReadOutlined />}>
                                Reader Mode
                            </Button>
                        </Link>
                        <Dropdown
                            trigger={['click']}
                            menu={{
                                items: [
                                    {
                                        key: 'logout',
                                        icon: <LogoutOutlined />,
                                        label: 'Logout',
                                        onClick: async () => {
                                            await logout();
                                            navigate('/');
                                        }
                                    }
                                ]
                            }}
                        >
                            <Button icon={<UserOutlined />}>
                                {user?.username || 'Account'} <DownOutlined />
                            </Button>
                        </Dropdown>
                    </div>
                </Header>
                {loading ? (
                    <Content style={{ padding: '50px 50px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
                        <div style={{ color: '#888', fontSize: 14 }}>Loading...</div>
                    </Content>
                ) : (
                    <Content key={`${book}-${chapter}-${lang}`} className="content-fade-in" style={{ padding: '50px 50px' }}>
                        <div className="site-layout-content">
                            <Dropdown
                                trigger={['click']}
                                onOpenChange={(open) => {
                                    if (!open) setBookFilter('');
                                }}
                                overlay={
                                    <div style={{ background: '#fff', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                                        <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                                            <Input
                                                placeholder="Search books..."
                                                value={bookFilter}
                                                onChange={e => setBookFilter(e.target.value)}
                                                onClick={e => e.stopPropagation()}
                                                onKeyDown={e => e.stopPropagation()}
                                                allowClear
                                                size="small"
                                                autoFocus
                                            />
                                        </div>
                                        <Menu style={{ maxHeight: '50vh', overflowY: 'auto', boxShadow: 'none', border: 'none' }}>
                                        {ot_books.filter(b =>
                                            getBookDisplayName(b).toLowerCase().includes(bookFilter.toLowerCase())
                                        ).length > 0 && (
                                            <Menu.ItemGroup title="Old Testament (Hebrew)">
                                                {ot_books
                                                    .filter(b => getBookDisplayName(b).toLowerCase().includes(bookFilter.toLowerCase()))
                                                    .map(b =>
                                                        <Menu.Item key={b}>
                                                            <a
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    setLoading(true);
                                                                    setBook(b);
                                                                    setChapter(1);
                                                                    setVerse(1);
                                                                    clearSelection();
                                                                    setLang("hebrew");
                                                                    setBookFilter('');
                                                                    setRefreshKey(refreshKey + 1);
                                                                }}
                                                            >
                                                                {getBookDisplayName(b)}
                                                            </a>
                                                        </Menu.Item>
                                                    )}
                                            </Menu.ItemGroup>
                                        )}
                                        {nt_books.filter(b =>
                                            getBookDisplayName(b).toLowerCase().includes(bookFilter.toLowerCase())
                                        ).length > 0 && (
                                            <Menu.ItemGroup title="New Testament (Greek)">
                                                {nt_books
                                                    .filter(b => getBookDisplayName(b).toLowerCase().includes(bookFilter.toLowerCase()))
                                                    .map(b =>
                                                        <Menu.Item key={b}>
                                                            <a
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    setLoading(true);
                                                                    setBook(b);
                                                                    setChapter(1);
                                                                    setVerse(1);
                                                                    clearSelection();
                                                                    setLang("greek");
                                                                    setBookFilter('');
                                                                    setRefreshKey(refreshKey + 1);
                                                                }}
                                                            >
                                                                {getBookDisplayName(b)}
                                                            </a>
                                                        </Menu.Item>
                                                    )}
                                            </Menu.ItemGroup>
                                        )}
                                        {ot_books.filter(b => getBookDisplayName(b).toLowerCase().includes(bookFilter.toLowerCase())).length === 0 &&
                                         nt_books.filter(b => getBookDisplayName(b).toLowerCase().includes(bookFilter.toLowerCase())).length === 0 && (
                                            <Menu.Item disabled>
                                                <span style={{ color: '#999' }}>No books found</span>
                                            </Menu.Item>
                                        )}
                                        </Menu>
                                    </div>
                                }
                            >
                                <span style={{ cursor: "pointer", marginRight: 20, fontSize: 20, fontWeight: 600 }}>{getBookDisplayName(book)} <DownOutlined /></span>
                            </Dropdown>
                            <Dropdown
                                trigger={['click']}
                                overlay={
                                    <Menu>
                                        {totalChapters.map(c =>
                                            <Menu.Item key={c.chapter}>
                                                <a
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        setRefreshKey(refreshKey + 1);
                                                        setChapter(c.chapter);
                                                        setVerse(1);
                                                        clearSelection();
                                                    }}
                                                >
                                                    {c.chapter}
                                                </a>
                                            </Menu.Item>
                                        )}
                                        <Menu.Divider />
                                        <Menu.Item key="new-chapter">
                                            <a
                                                onClick={e => {
                                                    e.preventDefault();
                                                    const nextChapter = totalChapters.length > 0
                                                        ? Math.max(...totalChapters.map(c => c.chapter)) + 1
                                                        : 1;
                                                    setChapter(nextChapter);
                                                    setVerse(1);
                                                    setRefreshKey(refreshKey + 1);
                                                    clearSelection();
                                                }}
                                                style={{ fontStyle: 'italic', color: '#1890ff' }}
                                            >
                                                + New Chapter ({totalChapters.length > 0 ? Math.max(...totalChapters.map(c => c.chapter)) + 1 : 1})
                                            </a>
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <span style={{ cursor: "pointer", marginRight: 20, fontSize: 20, fontWeight: 600 }}>{`Chapter ${chapter}`} <DownOutlined /></span>
                            </Dropdown>
                            <Dropdown
                                trigger={['click']}
                                overlay={
                                    <Menu>
                                        {totalVerses.map(v =>
                                            <Menu.Item key={v}>
                                                <a
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        setVerse(v);
                                                        clearSelection();
                                                    }}
                                                >
                                                    {v}
                                                </a>
                                            </Menu.Item>
                                        )}
                                        <Menu.Divider />
                                        <Menu.Item key="new-verse">
                                            <a
                                                onClick={e => {
                                                    e.preventDefault();
                                                    const nextVerse = totalVerses.length > 0
                                                        ? Math.max(...totalVerses) + 1
                                                        : 1;
                                                    setVerse(nextVerse);
                                                    clearSelection();
                                                }}
                                                style={{ fontStyle: 'italic', color: '#1890ff' }}
                                            >
                                                + New Verse ({totalVerses.length > 0 ? Math.max(...totalVerses) + 1 : 1})
                                            </a>
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <span style={{ cursor: "pointer", fontSize: 20, fontWeight: 600 }}>{`Verse ${verse}`} <DownOutlined /></span>
                            </Dropdown>
                            <span style={{ marginLeft: 30 }}>
                                <Switch
                                    checked={showManuscript}
                                    onChange={setShowManuscript}
                                    size="small"
                                />
                                <span style={{ marginLeft: 8, fontSize: 13, color: '#666' }}>Show Exemplar</span>
                            </span>
                            {/* Exemplar display (when toggled on) - styled like an ancient scroll */}
                            {showManuscript && (
                                <div style={{
                                    marginTop: 20,
                                    padding: window.innerWidth < 600 ? '12px 12px' : '20px 24px',
                                    background: 'linear-gradient(135deg, #F2EFE4 0%, #F5EFE4 50%, #F2E8D5 100%)',
                                    border: '2px solid #c9b896',
                                    borderRadius: 8,
                                    boxShadow: 'inset 0 0 30px rgba(139, 119, 82, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
                                    position: 'relative',
                                    overflowX: 'hidden'
                                }}>
                                    {/* Decorative scroll edges */}
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: 4,
                                        background: 'linear-gradient(to bottom, rgba(139, 119, 82, 0.2), transparent)',
                                        borderRadius: '8px 8px 0 0'
                                    }} />
                                    <div style={{
                                        fontSize: 11,
                                        color: '#8b7752',
                                        marginBottom: 12,
                                        textTransform: 'uppercase',
                                        letterSpacing: 2,
                                        fontWeight: 600,
                                        borderBottom: '1px solid #d4c4a8',
                                        paddingBottom: 8
                                    }}>
                                        Exemplar
                                    </div>
                                    <div
                                        className={`${lang} ${lang === 'greek' ? 'greek-content' : 'hebrew-content'}`}
                                        style={{
                                            fontSize: lang === 'hebrew' ? 'clamp(24px, 6vw, 38px)' : 'clamp(18px, 5vw, 30px)',
                                            lineHeight: 1.8,
                                            color: '#3d3425',
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            fontFamily: lang === 'hebrew' ? 'TaameyAshkenaz, serif' : undefined
                                        }}
                                    >
                                        {(() => {
                                            // Filter to only show the current verse, sorted by position
                                            const currentVerseManuscript = manuscriptData
                                                .filter(w => w.verse === verse)
                                                .sort((a, b) => a.position - b.position);
                                            if (currentVerseManuscript.length > 0) {
                                                return (
                                                    <span>
                                                        <sup style={{ fontSize: 12, marginRight: 4, color: '#8b7752' }}><b>{verse}</b></sup>
                                                        {currentVerseManuscript.map((w, idx) => {
                                                            // Check if this is a punctuation entry
                                                            const isPunctuation = w.is_punctuation === 1;
                                                            // Handle maqaf (־) word connections
                                                            const thisEndsMaqaf = w.word.endsWith('־');
                                                            const isHighlighted = !isPunctuation && highlightPosition === w.position;

                                                            // Punctuation: just render it directly without tooltip or click handler
                                                            if (isPunctuation) {
                                                                return (
                                                                    <span key={`${w.verse}-${w.position}`} style={{ color: '#5a4a3a' }}>
                                                                        {w.word}
                                                                    </span>
                                                                );
                                                            }

                                                            return (
                                                            <Tooltip key={`${w.verse}-${w.position}`} title="Click to see character breakdown">
                                                                    <span
                                                                        onClick={() => {
                                                                            setCharAnalysisWord(w.word);
                                                                            setIsCharModalVisible(true);
                                                                        }}
                                                                        style={{
                                                                            backgroundColor: isHighlighted ? '#fff4d9' : 'transparent',
                                                                            padding: isHighlighted ? '2px 6px' : '2px 4px',
                                                                            // Pull next word close if this ends with maqaf (in RTL, marginInlineEnd = left side)
                                                                            marginInlineEnd: thisEndsMaqaf ? '-0.3em' : undefined,
                                                                            borderRadius: 3,
                                                                            border: isHighlighted ? '2px solid #d4a84b' : 'none',
                                                                            boxShadow: isHighlighted ? '0 0 8px rgba(212, 168, 75, 0.4)' : 'none',
                                                                            cursor: 'pointer',
                                                                            transition: 'background-color 0.2s',
                                                                            position: 'relative',
                                                                            zIndex: isHighlighted ? 10 : 1
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            if (!isHighlighted) {
                                                                                e.currentTarget.style.backgroundColor = '#f5edd6';
                                                                                e.currentTarget.style.zIndex = '10';
                                                                            }
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            if (!isHighlighted) {
                                                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                                                e.currentTarget.style.zIndex = '1';
                                                                            }
                                                                        }}
                                                                    >
                                                                        {w.word}
                                                                    </span>
                                                                </Tooltip>
                                                        );})}
                                                    </span>
                                                );
                                            } else {
                                                return (
                                                    <span style={{ color: '#a89870', fontStyle: 'italic', fontFamily: 'inherit' }}>
                                                        No exemplar data available for this verse.
                                                    </span>
                                                );
                                            }
                                        })()}
                                    </div>
                                    <div style={{ marginTop: 14, fontSize: 10, color: '#a89870', borderTop: '1px solid #d4c4a8', paddingTop: 10 }}>
                                        {lang === 'hebrew' ? (
                                            <>
                                                Source: The Hebrew Old Testament, Westminster Leningrad Codex.{' '}
                                                <a href="https://ebible.org/hboWLC/copyright.htm" target="_blank" rel="noopener noreferrer">
                                                    Copyright info (Public Domain)
                                                </a>
                                            </>
                                        ) : (
                                            <>
                                                Source: The New Testament in Koine Greek, based on Tischendorf's 8th edition.{' '}
                                                <a href="https://ebible.org/grc-tisch/copyright.htm" target="_blank" rel="noopener noreferrer">
                                                    Copyright info (Public Domain)
                                                </a>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: 20 }}>
                                <LeftOutlined
                                    onClick={goToPrevVerse}
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: 24,
                                        padding: 10,
                                        opacity: (verse === totalVerses[0] && chapter === 1) ? 0.3 : 1
                                    }}
                                />
                                <h3
                                    className={`${lang} ${lang === 'greek' ? 'greek-content' : 'hebrew-content'}`}
                                    style={{ flex: 1, margin: '0 20px', textAlign: 'center' }}
                                >
                                    {currentVerseWords.length > 0 ? (
                                        <>
                                            <sup><b>{verse}</b></sup>{' '}
                                            {currentVerseWords.map((v: any, idx: number) => {
                                                // Don't add space after maqaf (־)
                                                const prevWord = idx > 0 ? currentVerseWords[idx - 1].word : '';
                                                const addSpace = idx > 0 && !prevWord.endsWith('־');
                                                return (
                                                <span key={v.id}>
                                                    {addSpace && ' '}
                                                    <WordLink
                                                        verseData={v}
                                                        setDefinition={setDefinition}
                                                        setSelectedOccurrence={setSelectedOccurrence}
                                                        lang={lang}
                                                        setHighlightPosition={setHighlightPosition}
                                                    />
                                                </span>
                                            );})}
{/* Punctuation is now stored as separate entries in manuscript data, no need to add manually */}
                                        </>
                                    ) : (
                                        <span style={{
                                            color: '#888',
                                            fontStyle: 'italic',
                                            fontSize: 16,
                                            fontFamily: 'inherit',
                                            direction: 'ltr',
                                            unicodeBidi: 'isolate'
                                        }}>
                                            No words logged. Add the first word below.
                                        </span>
                                    )}
                                </h3>
                                <RightOutlined
                                    onClick={goToNextVerse}
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: 24,
                                        padding: 10,
                                        opacity: (verse === totalVerses[totalVerses.length - 1] && chapter === totalChapters[totalChapters.length - 1]?.chapter) ? 0.3 : 1
                                    }}
                                />
                            </div>
                        </div>
                        {Object.keys(definition).length > 0 &&
                            <>
                                <Divider>Definition</Divider>
                                <Table
                                    dataSource={[{
                                        ...definition,
                                        word: selectedOccurrence?.word,
                                        definition_in_context: selectedOccurrence?.definition_in_context
                                    }]}
                                    columns={filterColumns({
                                        ...definition,
                                        word: selectedOccurrence?.word,
                                        definition_in_context: selectedOccurrence?.definition_in_context
                                    }, lang, selectedOccurrence?.word)}
                                    pagination={false}
                                    rowKey="word"
                                    size="small"
                                />
                                <div style={{ marginTop: 16, textAlign: 'center', display: 'flex', gap: 8, justifyContent: 'center' }}>
                                    <Button type="primary" onClick={handleUpdateClick}>
                                        Edit This Word
                                    </Button>
                                    <Button onClick={clearSelection}>
                                        Cancel
                                    </Button>
                                </div>
                            </>
                        }
                        {/* Show completion message or Add New Word form (hidden when viewing/editing a word) */}
                        {!selectedOccurrence && (() => {
                            const exemplarVerseWords = manuscriptData.filter(w => w.verse === verse);
                            const maxExemplarPosition = exemplarVerseWords.length > 0
                                ? Math.max(...exemplarVerseWords.map(w => w.position))
                                : 0;
                            const loggedPositions = currentVerseWords.length;
                            const isVerseComplete = showManuscript && maxExemplarPosition > 0 && loggedPositions >= maxExemplarPosition;

                            if (isVerseComplete) {
                                return (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '32px 16px',
                                        // background: 'linear-gradient(135deg, #f5f0e6 0%, #e8dcc8 100%)',
                                        // borderRadius: 8,
                                        // border: '2px solid #c9b896',
                                        marginTop: 16
                                    }}>
                                        <div style={{ fontSize: 32, marginBottom: 8 }}>📜 ✨</div>
                                        <div style={{
                                            fontSize: 18,
                                            fontWeight: 600,
                                            color: '#5d4e37',
                                            fontFamily: 'Georgia, serif'
                                        }}>
                                            Verse Transcription Complete
                                        </div>
                                        <div style={{
                                            fontSize: 14,
                                            color: '#7a6b54',
                                            marginTop: 8,
                                            fontStyle: 'italic'
                                        }}>
                                            Well done, faithful scribe! This verse has been fully copied.
                                        </div>
                                        <div style={{
                                            fontSize: 12,
                                            color: '#998a73',
                                            marginTop: 12
                                        }}>
                                            Click any word above to edit if needed.
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <>
                                    <Divider>Add New Word</Divider>
                                    <InputForm
                                        verses={currentVerseWords}
                                        book={book}
                                        chapter={chapter}
                                        verse={verse}
                                        lang={lang}
                                        refreshKey={refreshKey}
                                        setRefreshKey={setRefreshKey}
                                        maxPosition={showManuscript ? maxExemplarPosition || undefined : undefined}
                                        onPositionChange={setHighlightPosition}
                                        manuscriptData={manuscriptData.filter(w => w.verse === verse)}
                                    />
                                </>
                            );
                        })()}

                        {/* Edit Word Modal */}
                        <Modal
                            title="Edit Word"
                            open={isEditModalVisible}
                            onCancel={() => {
                                setIsEditModalVisible(false);
                                setEditData(null);
                            }}
                            footer={null}
                            width={900}
                            destroyOnClose
                        >
                            <InputForm
                                verses={currentVerseWords}
                                book={book}
                                chapter={chapter}
                                verse={verse}
                                lang={lang}
                                refreshKey={refreshKey}
                                setRefreshKey={setRefreshKey}
                                editData={editData}
                                clearEditData={clearSelection}
                                isModal={true}
                                onClose={() => {
                                    setIsEditModalVisible(false);
                                    setEditData(null);
                                    clearSelection();
                                }}
                                onPositionChange={setHighlightPosition}
                            />
                        </Modal>

                        {/* Character Analysis Modal */}
                        <Modal
                            title={
                                <div style={{ textAlign: 'center' }}>
                                    <span style={{
                                        fontSize: '28px',
                                        fontFamily: lang === 'hebrew' ? 'TaameyAshkenaz, serif' : 'SBL Greek, Gentium Plus, serif',
                                        direction: lang === 'hebrew' ? 'rtl' : 'ltr'
                                    }}>
                                        {charAnalysisWord}
                                    </span>
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                        Character Breakdown
                                    </div>
                                </div>
                            }
                            open={isCharModalVisible}
                            onCancel={() => {
                                setIsCharModalVisible(false);
                                setCharAnalysisWord(null);
                            }}
                            footer={null}
                            width={600}
                            destroyOnClose
                        >
                            {charAnalysisWord && (
                                <div style={{
                                    background: 'linear-gradient(135deg, #f5f0e6 0%, #e8dcc8 50%, #f2ebe0 100%)',
                                    border: '2px solid #c4a35a',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    boxShadow: 'inset 0 2px 8px rgba(139, 90, 43, 0.15)'
                                }}>
                                    {/* Legend */}
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                        marginBottom: '16px',
                                        padding: '10px',
                                        background: 'rgba(255,255,255,0.5)',
                                        borderRadius: '6px',
                                        fontSize: '11px'
                                    }}>
                                        {(['consonant', 'vowel', 'cantillation', 'punctuation', 'breathing', 'accent', 'other'] as const).map(type => (
                                            <span key={type} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '3px',
                                                    background: getTypeColor(type),
                                                    display: 'inline-block'
                                                }}></span>
                                                {getTypeLabel(type)}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Character breakdown */}
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                        direction: lang === 'hebrew' ? 'rtl' : 'ltr',
                                        justifyContent: 'center'
                                    }}>
                                        {parseWordCharacters(charAnalysisWord, lang as 'hebrew' | 'greek').map((charInfo, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    background: 'white',
                                                    border: `2px solid ${getTypeColor(charInfo.type)}`,
                                                    borderRadius: '8px',
                                                    padding: '12px 16px',
                                                    minWidth: '90px',
                                                    textAlign: 'center',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                }}
                                            >
                                                {/* The character */}
                                                <div style={{
                                                    fontSize: '36px',
                                                    fontFamily: lang === 'hebrew' ? 'TaameyAshkenaz, serif' : 'SBL Greek, Gentium Plus, serif',
                                                    lineHeight: 1.2,
                                                    color: '#3d2914'
                                                }}>
                                                    {charInfo.char}
                                                </div>

                                                {/* Type badge */}
                                                <div style={{
                                                    background: getTypeColor(charInfo.type),
                                                    color: 'white',
                                                    fontSize: '9px',
                                                    padding: '2px 6px',
                                                    borderRadius: '10px',
                                                    marginTop: '4px',
                                                    display: 'inline-block'
                                                }}>
                                                    {getTypeLabel(charInfo.type)}
                                                </div>

                                                {/* Name */}
                                                <div style={{
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    color: '#5a4a3a',
                                                    marginTop: '8px'
                                                }}>
                                                    {charInfo.name}
                                                </div>

                                                {/* Phonetic */}
                                                {charInfo.phonetic && (
                                                    <div style={{
                                                        fontSize: '11px',
                                                        color: '#2e7d32',
                                                        fontStyle: 'italic',
                                                        marginTop: '4px'
                                                    }}>
                                                        /{charInfo.phonetic}/
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Transliteration */}
                                    <div style={{
                                        marginTop: '20px',
                                        padding: '12px',
                                        background: 'rgba(255,255,255,0.6)',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        color: '#5a4a3a'
                                    }}>
                                        <strong>Transliteration:</strong>{' '}
                                        <span style={{ fontFamily: 'serif', fontSize: '16px', letterSpacing: '0.5px' }}>
                                            {getTransliteration(charAnalysisWord, lang as 'hebrew' | 'greek') || 'N/A'}
                                        </span>
                                    </div>

                                    {/* Pronunciation guide */}
                                    <div style={{
                                        marginTop: '10px',
                                        padding: '12px',
                                        background: 'rgba(255,255,255,0.6)',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        color: '#5a4a3a'
                                    }}>
                                        <strong>Pronunciation Guide:</strong>{' '}
                                        {parseWordCharacters(charAnalysisWord, lang as 'hebrew' | 'greek')
                                            .filter(c => c.phonetic)
                                            .map(c => c.phonetic)
                                            .join('-') || 'No phonetic data available'}
                                    </div>
                                </div>
                            )}
                        </Modal>
                    </Content>
                )}
                <Footer style={{ textAlign: 'center' }}>NotByBreadAlone.io | <a href="mailto:notbybreadalone.io@gmail.com">Contact</a></Footer>
            </Layout>
        </div >
    );
}

export default Populate;
